import { format } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { match } from 'ts-pattern';

import { CurrencyConvertor } from '~/components/utils/formatters';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Text } from '~/domains/design-system/Text';

import type { LoyaltyTier } from '../../../../types/gamification.types';
import type { TierStatus } from '../../types/whalersClub.types';
import { formatPeriodDeadline } from '../../utils/formatPeriodDeadline';
import WhalersClubProgressBar from '../WhalersClubProgressBar/WhalersClubProgressBar';
import WhalersClubFaqAccordion from '../WhalersClubFaqAccordion/WhalersClubFaqAccordion';
import { TIER_COLORS } from '../../constants';

import { useStyles } from './WhalersClubDetailModal.styles';
import WhalersClubTierBadge from './WhalersClubTierBadge/WhalersClubTierBadge';

interface WhalersClubDetailModalProps {
  isOpen: boolean;
  isClosing: boolean;
  onClose: () => void;
  handleOpenHistoryModal: () => void;
  tiers: LoyaltyTier[];
  tierStatus: TierStatus;
  faq: string | null;
  whatQualifies: string;
  whatDoesNotQualify: string;
}

function WhalersClubDetailModal({
  isOpen,
  isClosing,
  onClose,
  handleOpenHistoryModal,
  tiers,
  tierStatus,
  faq,
  whatQualifies,
  whatDoesNotQualify,
}: WhalersClubDetailModalProps) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation('loyalty');

  const {
    rewardBasis,
    currentTierName,
    currentTierIndex,
    currentProgressAmount,
    periodEndAt,
    nextTierRemainingAmount,
    isLastTier,
  } = tierStatus;

  const isDark = isLastTier;
  const deadline = match(rewardBasis)
    .with('handle', () => t('whalersClub.detailModal.deadline', {
      deadline: format(periodEndAt, 'EEEE, h:mm a'),
    }))
    .with('net_loss', () => t('whalersClub.detailModal.deadlineLossBack', {
      deadline: formatPeriodDeadline(periodEndAt, 'EEE, h:mm a'),
    }))
    .exhaustive();

  // Specific color override for the initial tier once the progress is started
  const { modalGradient } = TIER_COLORS[currentTierName] || TIER_COLORS.Default;

  return (
    <Dialog
      type={{ desktop: 'modal', mobile: 'drawer' }}
      isOpen={isOpen}
      isClosing={isClosing}
      onClose={onClose}
      className={cx(classes.dailogContainer, [isDark && 'isDark'])}
    >
      <DialogBody className={classes.dialogBody}>
        <div className={classes.dialogGradient} style={{ background: modalGradient }} />
        <div className={classes.tierBadgeContainer}>
          <WhalersClubTierBadge tierIndex={currentTierIndex} />
        </div>

        <div className={classes.contentWrapper}>
          <div className={classes.statusContainer}>
            <Text variant="subhead-small" display="block">
              {currentTierName
                ? t('whalersClub.detailModal.currentPlaythrough', { amount: CurrencyConvertor(currentProgressAmount) })
                : t('whalersClub.detailModal.progressToTier', { amount: CurrencyConvertor(nextTierRemainingAmount), tierName: tiers[0].name })
              }
            </Text>
            <Text variant="subhead-small" display="block">
              {deadline}
            </Text>
          </div>
          <div className={classes.progressBarContainer}>
            <WhalersClubProgressBar tiers={tiers} tierStatus={tierStatus} />
          </div>
          <div className={classes.faqContainer}>
            <WhalersClubFaqAccordion
              rewardBasis={rewardBasis}
              tiers={tiers}
              faq={faq}
              whatQualifies={whatQualifies}
              whatDoesNotQualify={whatDoesNotQualify}
              isDarkMode={currentTierIndex === tiers.length - 1}
              handleOpenHistoryModal={handleOpenHistoryModal}
            />
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default WhalersClubDetailModal;
